import "./style.scss";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  InputAdornment,
  Alert,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Link, Navigate, redirect, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { EyeClosedIcon, EyeIcon, LeftArrowIcon, Logo, QatarIcon } from "../../assets/icons";
import { checkIf, checkEmptyObj, getUserRole, UserRoles } from "../../utils";
import useLogin from "../Auth/useLogin";
import { RootState } from "../../store";
import useValidation from "../Validation";

interface IError {
  show?: boolean;
  message?: string;
}

export interface IFieldValidate {
  status?: boolean;
  message?: string;
  dirty?: boolean;
}
export interface ILoginValidate {
  show?: boolean;
  password?: IFieldValidate;
  email?: IFieldValidate;
}

export interface ILogin {
  email?: string;
  password?: string;
}

export default function Login() {
  let [searchParams, setSearchParams] = useSearchParams();
  const invitationCode = searchParams.get("code");
  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhone] = useState("");
  const [areaCode, setAreaCode] = useState("+974");
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useLogin();
  const [errorObj, setError] = useState<IError>();
  const [loading, setLoading] = useState(false);

  const [userInfo, setUserInfo] = useState<ILogin>({
    email: "",
    password: "",
  });

  const setField = (field: string, value: string) => {
    setUserInfo((state) => ({
      ...state,
      [field]: value,
    }));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const resetForm = () => {
    setUserInfo({ email: "", password: "" });
  };

  const handleLogin = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    try {
      setLoading(true);
      const user = await login({ password: String(userInfo.password), email: String(userInfo.email) });
      if (user) {
        resetForm();
        const userRole = getUserRole(user);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message;
      setError({ show: true, message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const { validation, setValidation } = useValidation(userInfo);

  return (
    <Box sx={{ padding: ["0 16px", "32px"] }} className="login_wrapper" component="form" noValidate>
      <Box mb={6} sx={{ display: "flex", justifyContent: "center" }}>
        <Logo />
      </Box>
      <div className="title">Sign in to your Account</div>
      <div className="subtitle">Please enter your email and password to login.</div>
      <div className="phone_container">
        <TextField
          value={userInfo?.email}
          placeholder="E-Mail Adress"
          error={validation.show && validation.email?.error}
          helperText={validation.show && validation.email?.error ? validation.email.helperText : undefined}
          onChange={(e) => setField("email", e.target.value)}
        />
      </div>
      <div className="password_container">
        <TextField
          type={showPassword ? "text" : "password"}
          className="text-input"
          value={userInfo?.password}
          placeholder={`Password`}
          error={validation?.show && validation.password?.error}
          helperText={validation?.show && validation?.password?.error ? validation?.password?.helperText : undefined}
          onChange={(e) => setField("password", e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((show) => !show)}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div className="options">
        <div className="remember">
          <FormControlLabel control={<Checkbox />} label="Remember Me" />
        </div>
        {/* <div className="forgot">
          <Link to={`/forgot-password`}>Forgot Password</Link>
        </div> */}
      </div>
      <div className="login-btn">
        {errorObj?.show && (
          <Alert severity="error" sx={{ marginBottom: "8px" }}>
            {errorObj?.message}
          </Alert>
        )}
        <LoadingButton
          type="submit"
          color="primary"
          loading={loading}
          variant="contained"
          disableElevation
          onClick={handleLogin}
        >
          {loading ? <span>&nbsp;</span> : <span>Login</span>}
        </LoadingButton>
      </div>
    </Box>
  );
}
